import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  useColorMode,
  Link,
  Divider,
  Image,
  Circle,
} from "@chakra-ui/react";
import { SEOHead } from "../seometadata/components/SEOHead";

const BdayPage = () => {
  const { colorMode } = useColorMode();

  // Elegant boutique colour palette
  const champagne = "#F7E7CE";
  const softGold = "#D4AF37";
  const warmWhite = "#FAF9F6";
  const deepCharcoal = "#1C1C1C";
  const mutedTeal = "#5B8A8A";

  const bgGradient = colorMode === 'light'
    ? `linear-gradient(180deg, ${warmWhite} 0%, ${champagne}40 100%)`
    : `linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)`;

  const cardBg = colorMode === 'light'
    ? "rgba(255, 255, 255, 0.9)"
    : "rgba(30, 30, 46, 0.95)";

  const textPrimary = colorMode === 'light' ? deepCharcoal : "#F5F5F5";
  const textSecondary = colorMode === 'light' ? "#666666" : "#B0B0B0";
  const accentColor = colorMode === 'light' ? softGold : "#E8D5A3";

  return (
    <Box
      bg={bgGradient}
      minHeight="100vh"
      position="relative"
      overflow="hidden"
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: colorMode === 'light'
          ? `radial-gradient(60% 60% at 80% 20%, ${champagne}40 0%, transparent 50%)`
          : `radial-gradient(60% 60% at 80% 20%, rgba(212, 175, 55, 0.1) 0%, transparent 50%)`,
        pointerEvents: 'none',
        zIndex: 0,
      }}
      sx={{
        '@keyframes shimmer': {
          '0%': {
            backgroundPosition: '200% center',
          },
          '100%': {
            backgroundPosition: '-200% center',
          }
        },
        '@keyframes shimmer-vertical': {
          '0%': {
            backgroundPosition: 'center 200%',
          },
          '100%': {
            backgroundPosition: 'center -200%',
          }
        },
        '@keyframes sparkle': {
          '0%': {
            transform: 'translateX(-200px) translateY(-100px)',
            opacity: 0,
          },
          '5%': {
            opacity: 0.4,
          },
          '25%': {
            transform: 'translateX(25vw) translateY(25vh)',
            opacity: 0.8,
          },
          '50%': {
            transform: 'translateX(50vw) translateY(50vh)',
            opacity: 1,
          },
          '75%': {
            transform: 'translateX(75vw) translateY(75vh)',
            opacity: 0.8,
          },
          '95%': {
            opacity: 0.4,
          },
          '100%': {
            transform: 'translateX(calc(100vw + 200px)) translateY(100vh)',
            opacity: 0,
          }
        },
        '@keyframes sparkle-slow': {
          '0%': {
            transform: 'translateX(-250px) translateY(-100px)',
            opacity: 0,
          },
          '5%': {
            opacity: 0.3,
          },
          '30%': {
            transform: 'translateX(35vw) translateY(30vh)',
            opacity: 0.6,
          },
          '60%': {
            transform: 'translateX(65vw) translateY(60vh)',
            opacity: 0.8,
          },
          '95%': {
            opacity: 0.3,
          },
          '100%': {
            transform: 'translateX(calc(100vw + 250px)) translateY(100vh)',
            opacity: 0,
          }
        },
        '@keyframes sparkle-medium': {
          '0%': {
            transform: 'translateX(-180px) translateY(-100px)',
            opacity: 0,
          },
          '8%': {
            opacity: 0.5,
          },
          '40%': {
            transform: 'translateX(45vw) translateY(40vh)',
            opacity: 0.9,
          },
          '70%': {
            transform: 'translateX(80vw) translateY(70vh)',
            opacity: 0.7,
          },
          '92%': {
            opacity: 0.4,
          },
          '100%': {
            transform: 'translateX(calc(100vw + 180px)) translateY(100vh)',
            opacity: 0,
          }
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-10%',
          width: '120%',
          height: '100%',
          backgroundImage: colorMode === 'light'
            ? `
              radial-gradient(3px 3px at 10% 10%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(212,175,55,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(212,175,55,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(212,175,55,0.6) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(212,175,55,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(212,175,55,0.5) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(212,175,55,0.8) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(212,175,55,0.4) 50%, transparent 50%)
            `
            : `
              radial-gradient(3px 3px at 10% 10%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 30%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 15% 50%, rgba(232,213,163,0.6) 50%, transparent 50%),
              radial-gradient(5px 5px at 25% 20%, rgba(232,213,163,0.3) 50%, transparent 50%),
              radial-gradient(3px 3px at 30% 60%, rgba(232,213,163,0.5) 50%, transparent 50%),
              radial-gradient(6px 6px at 35% 40%, rgba(232,213,163,0.25) 50%, transparent 50%),
              radial-gradient(4px 4px at 40% 70%, rgba(232,213,163,0.4) 50%, transparent 50%),
              radial-gradient(2px 2px at 45% 15%, rgba(232,213,163,0.7) 50%, transparent 50%),
              radial-gradient(5px 5px at 50% 80%, rgba(232,213,163,0.35) 50%, transparent 50%)
            `,
          backgroundSize: '400px 400px, 500px 500px, 350px 350px, 450px 450px, 550px 550px, 600px 600px, 480px 480px, 420px 420px, 520px 520px',
          backgroundPosition: '0 0, 80px 60px, 160px 40px, 240px 100px, 320px 160px, 400px 80px, 480px 200px, 560px 20px, 640px 180px',
          animation: 'sparkle 25s linear infinite',
          pointerEvents: 'none',
          zIndex: 1,
        }
      }}
    >
      {/* Sparkle effect layers */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        pointerEvents="none"
        zIndex={1}
      >
        {/* Second sparkle layer - slower and larger */}
        <Box
          position="absolute"
          top={0}
          left="-20%"
          width="140%"
          height="100%"
          opacity={0.5}
          sx={{
            backgroundImage: colorMode === 'light'
              ? `
                radial-gradient(5px 5px at 5% 15%, rgba(212,175,55,0.4) 50%, transparent 50%),
                radial-gradient(3px 3px at 15% 35%, rgba(212,175,55,0.5) 50%, transparent 50%),
                radial-gradient(7px 7px at 10% 55%, rgba(212,175,55,0.3) 50%, transparent 50%),
                radial-gradient(4px 4px at 20% 25%, rgba(212,175,55,0.6) 50%, transparent 50%),
                radial-gradient(8px 8px at 25% 45%, rgba(212,175,55,0.25) 50%, transparent 50%),
                radial-gradient(6px 6px at 30% 65%, rgba(212,175,55,0.45) 50%, transparent 50%),
                radial-gradient(3px 3px at 35% 20%, rgba(212,175,55,0.7) 50%, transparent 50%)
              `
              : `
                radial-gradient(5px 5px at 5% 15%, rgba(232,213,163,0.35) 50%, transparent 50%),
                radial-gradient(3px 3px at 15% 35%, rgba(232,213,163,0.4) 50%, transparent 50%),
                radial-gradient(7px 7px at 10% 55%, rgba(232,213,163,0.25) 50%, transparent 50%),
                radial-gradient(4px 4px at 20% 25%, rgba(232,213,163,0.5) 50%, transparent 50%),
                radial-gradient(8px 8px at 25% 45%, rgba(232,213,163,0.2) 50%, transparent 50%),
                radial-gradient(6px 6px at 30% 65%, rgba(232,213,163,0.4) 50%, transparent 50%),
                radial-gradient(3px 3px at 35% 20%, rgba(232,213,163,0.6) 50%, transparent 50%)
              `,
            backgroundSize: '600px 600px, 700px 700px, 550px 550px, 650px 650px, 750px 750px, 680px 680px, 580px 580px',
            backgroundPosition: '0 0, 120px 140px, 240px 80px, 360px 200px, 480px 280px, 600px 120px, 720px 40px',
            animation: 'sparkle-slow 35s linear infinite',
          }}
        />

        {/* Third sparkle layer - medium speed */}
        <Box
          position="absolute"
          top={0}
          left="-15%"
          width="130%"
          height="100%"
          opacity={0.4}
          sx={{
            backgroundImage: colorMode === 'light'
              ? `
                radial-gradient(4px 4px at 8% 20%, rgba(212,175,55,0.5) 50%, transparent 50%),
                radial-gradient(6px 6px at 18% 40%, rgba(212,175,55,0.35) 50%, transparent 50%),
                radial-gradient(3px 3px at 12% 60%, rgba(212,175,55,0.6) 50%, transparent 50%),
                radial-gradient(5px 5px at 22% 30%, rgba(212,175,55,0.4) 50%, transparent 50%),
                radial-gradient(7px 7px at 28% 50%, rgba(212,175,55,0.3) 50%, transparent 50%),
                radial-gradient(4px 4px at 32% 70%, rgba(212,175,55,0.55) 50%, transparent 50%)
              `
              : `
                radial-gradient(4px 4px at 8% 20%, rgba(232,213,163,0.4) 50%, transparent 50%),
                radial-gradient(6px 6px at 18% 40%, rgba(232,213,163,0.3) 50%, transparent 50%),
                radial-gradient(3px 3px at 12% 60%, rgba(232,213,163,0.5) 50%, transparent 50%),
                radial-gradient(5px 5px at 22% 30%, rgba(232,213,163,0.35) 50%, transparent 50%),
                radial-gradient(7px 7px at 28% 50%, rgba(232,213,163,0.25) 50%, transparent 50%),
                radial-gradient(4px 4px at 32% 70%, rgba(232,213,163,0.45) 50%, transparent 50%)
              `,
            backgroundSize: '550px 550px, 650px 650px, 500px 500px, 600px 600px, 700px 700px, 580px 580px',
            backgroundPosition: '50px 30px, 150px 150px, 250px 90px, 350px 210px, 450px 270px, 550px 150px',
            animation: 'sparkle-medium 30s linear infinite',
          }}
        />
      </Box>

      <SEOHead
        pagePath="/bday"
        fallbackTitle="Tom's 40th - You're Invited"
        fallbackDescription="Join us in celebrating Tom's 40th Birthday. A poolside celebration on Saturday 14th March 2026."
      />

      <Container maxW="container.sm" py={{ base: 12, md: 20 }} px={{ base: 6, md: 8 }} position="relative" zIndex={2}>
        <VStack spacing={0} align="stretch">

          {/* Main Invitation Card */}
          <Box
            bg={cardBg}
            backdropFilter="blur(10px)"
            borderRadius="3xl"
            overflow="hidden"
            position="relative"
            shadow="2xl"
            border="1px solid"
            borderColor={colorMode === 'light' ? "rgba(212, 175, 55, 0.2)" : "rgba(232, 213, 163, 0.1)"}
          >
            {/* Shimmering top accent */}
            <Box
              h="3px"
              position="relative"
              overflow="hidden"
              bg={`linear-gradient(to right, transparent, ${accentColor}40, transparent)`}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg={colorMode === 'light'
                  ? `linear-gradient(90deg, transparent 0%, transparent 40%, ${softGold} 50%, transparent 60%, transparent 100%)`
                  : `linear-gradient(90deg, transparent 0%, transparent 40%, #E8D5A3 50%, transparent 60%, transparent 100%)`
                }
                backgroundSize="200% 100%"
                animation="shimmer 3s ease-in-out infinite"
              />
            </Box>

            {/* Shimmering left side accent */}
            <Box
              position="absolute"
              left={0}
              top={0}
              bottom={0}
              w="2px"
              overflow="hidden"
              bg={`linear-gradient(to bottom, transparent, ${accentColor}30, transparent)`}
              zIndex={1}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg={colorMode === 'light'
                  ? `linear-gradient(180deg, transparent 0%, transparent 40%, ${softGold} 50%, transparent 60%, transparent 100%)`
                  : `linear-gradient(180deg, transparent 0%, transparent 40%, #E8D5A3 50%, transparent 60%, transparent 100%)`
                }
                backgroundSize="100% 200%"
                animation="shimmer-vertical 4s ease-in-out infinite"
              />
            </Box>

            {/* Shimmering right side accent */}
            <Box
              position="absolute"
              right={0}
              top={0}
              bottom={0}
              w="2px"
              overflow="hidden"
              bg={`linear-gradient(to bottom, transparent, ${accentColor}30, transparent)`}
              zIndex={1}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg={colorMode === 'light'
                  ? `linear-gradient(180deg, transparent 0%, transparent 40%, ${softGold} 50%, transparent 60%, transparent 100%)`
                  : `linear-gradient(180deg, transparent 0%, transparent 40%, #E8D5A3 50%, transparent 60%, transparent 100%)`
                }
                backgroundSize="100% 200%"
                animation="shimmer-vertical 4s ease-in-out infinite"
                sx={{ animationDelay: '2s' }}
              />
            </Box>

            <Box py={{ base: 12, md: 16 }} px={{ base: 8, md: 12 }}>
              <VStack spacing={8}>

                {/* Decorative element */}
                <Text
                  fontSize="3xl"
                  opacity={0.8}
                  letterSpacing="0.5em"
                  color={accentColor}
                >
                  ✦
                </Text>

                {/* Photo of Tom */}
                <Circle
                  size={{ base: "140px", md: "180px" }}
                  overflow="hidden"
                  border="3px solid"
                  borderColor={accentColor}
                  bg={colorMode === 'light' ? "gray.100" : "gray.700"}
                >
                  <Image
                    src="/bday/truck_photo.png"
                    alt="Tom"
                    objectFit="cover"
                    w="100%"
                    h="100%"
                    fallback={
                      <VStack spacing={1}>
                        <Text fontSize="4xl">📷</Text>
                        <Text fontSize="xs" color={textSecondary}>Photo</Text>
                      </VStack>
                    }
                  />
                </Circle>

                {/* The Big Number */}
                <VStack spacing={0}>
                  <Text
                    fontSize={{ base: "7xl", md: "9xl" }}
                    fontWeight="200"
                    color={accentColor}
                    lineHeight="1"
                    letterSpacing="-0.02em"
                  >
                    40
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    textTransform="uppercase"
                    letterSpacing="0.4em"
                    color={textSecondary}
                    mt={2}
                  >
                    Years Young
                  </Text>
                </VStack>

                {/* Divider */}
                <Divider
                  w="60px"
                  borderColor={accentColor}
                  opacity={0.5}
                />

                {/* Invitation Text */}
                <VStack spacing={3}>
                  <Text
                    fontSize={{ base: "sm", md: "md" }}
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                  >
                    You're Invited
                  </Text>
                  <Heading
                    as="h1"
                    size={{ base: "lg", md: "xl" }}
                    color={textPrimary}
                    textAlign="center"
                    fontWeight="400"
                    letterSpacing="0.02em"
                  >
                    A Poolside Celebration
                  </Heading>
                </VStack>

                {/* Event Details */}
                <VStack spacing={6} pt={4}>

                  {/* Date */}
                  <VStack spacing={1}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.25em"
                      color={textSecondary}
                    >
                      Saturday
                    </Text>
                    <Text
                      fontSize={{ base: "2xl", md: "3xl" }}
                      fontWeight="300"
                      color={textPrimary}
                      letterSpacing="0.05em"
                    >
                      14th March 2026
                    </Text>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      color={textSecondary}
                      fontWeight="300"
                    >
                      From 2pm onwards
                    </Text>
                  </VStack>

                  {/* Location */}
                  <VStack spacing={3}>
                    <Text
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="0.25em"
                      color={textSecondary}
                    >
                      Location
                    </Text>
                    <Link
                      href="https://maps.app.goo.gl/jnXLwsynYS9eoP3E7"
                      isExternal
                      _hover={{ textDecoration: 'none' }}
                    >
                      <VStack
                        spacing={1}
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.2s"
                      >
                        <Text
                          fontSize={{ base: "md", md: "lg" }}
                          color={textPrimary}
                          fontWeight="300"
                        >
                          11 Athlone Street
                        </Text>
                        <Text
                          fontSize={{ base: "sm", md: "md" }}
                          color={textSecondary}
                          fontWeight="300"
                        >
                          Woolloongabba
                        </Text>
                      </VStack>
                    </Link>

                    {/* Embedded Map */}
                    <Box
                      w="full"
                      maxW="320px"
                      h="180px"
                      borderRadius="xl"
                      overflow="hidden"
                      border="1px solid"
                      borderColor={colorMode === 'light' ? "rgba(212, 175, 55, 0.3)" : "rgba(232, 213, 163, 0.15)"}
                      mt={2}
                    >
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d828.7928137691752!2d153.02585193695126!3d-27.489736674354635!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b915a720b57aa23%3A0xd670e4356da40d52!2s11%20Athlone%20St%2C%20Woolloongabba%20QLD%204101!5e1!3m2!1sen!2sau!4v1768005623413!5m2!1sen!2sau"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Event Location"
                      />
                    </Box>
                    <Link
                      href="https://maps.app.goo.gl/jnXLwsynYS9eoP3E7"
                      isExternal
                      fontSize="xs"
                      color={accentColor}
                      letterSpacing="0.1em"
                      _hover={{ opacity: 0.7 }}
                    >
                      Open in Maps →
                    </Link>
                  </VStack>

                </VStack>

                {/* Personal message */}
                <Box
                  py={6}
                  textAlign="center"
                  maxW="300px"
                >
                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color={textSecondary}
                    fontStyle="italic"
                    fontWeight="300"
                    lineHeight="1.8"
                  >
                    "Would love you to join me in celebrating 40 years around the sun."
                  </Text>
                </Box>

                {/* Divider */}
                <Divider
                  w="60px"
                  borderColor={accentColor}
                  opacity={0.5}
                />

                {/* RSVP Section */}
                <VStack spacing={4}>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    letterSpacing="0.3em"
                    color={textSecondary}
                  >
                    Kindly Respond
                  </Text>
                  <Link
                    href="sms:0458944327"
                    _hover={{ textDecoration: 'none' }}
                  >
                    <Box
                      bg="transparent"
                      color={accentColor}
                      px={10}
                      py={4}
                      border="1px solid"
                      borderColor={accentColor}
                      borderRadius="full"
                      fontWeight="400"
                      fontSize={{ base: "md", md: "lg" }}
                      letterSpacing="0.1em"
                      _hover={{
                        bg: accentColor,
                        color: colorMode === 'light' ? "white" : deepCharcoal,
                        transform: "translateY(-2px)",
                        shadow: "lg"
                      }}
                      transition="all 0.3s ease"
                      cursor="pointer"
                    >
                      0458 944 327
                    </Box>
                  </Link>
                  <Text
                    fontSize="xs"
                    color={textSecondary}
                    letterSpacing="0.1em"
                  >
                    Tap to send a text
                  </Text>
                </VStack>

                {/* Decorative element */}
                <Text
                  fontSize="3xl"
                  opacity={0.8}
                  letterSpacing="0.5em"
                  color={accentColor}
                  pt={4}
                >
                  ✦
                </Text>

              </VStack>
            </Box>

            {/* Shimmering bottom accent */}
            <Box
              h="3px"
              position="relative"
              overflow="hidden"
              bg={`linear-gradient(to right, transparent, ${accentColor}40, transparent)`}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg={colorMode === 'light'
                  ? `linear-gradient(90deg, transparent 0%, transparent 40%, ${softGold} 50%, transparent 60%, transparent 100%)`
                  : `linear-gradient(90deg, transparent 0%, transparent 40%, #E8D5A3 50%, transparent 60%, transparent 100%)`
                }
                backgroundSize="200% 100%"
                animation="shimmer 3s ease-in-out infinite"
                sx={{ animationDelay: '1.5s' }}
              />
            </Box>
          </Box>

          {/* Subtle footer note */}
          <HStack
            justify="center"
            pt={8}
            spacing={6}
            opacity={0.6}
          >
            <Text fontSize="xl">🏊</Text>
            <Text fontSize="xl">☀️</Text>
            <Text fontSize="xl">👨‍👩‍👧‍👦</Text>
            <Text fontSize="xl">🥂</Text>
          </HStack>
          <Text
            textAlign="center"
            fontSize="xs"
            color={textSecondary}
            pt={2}
            letterSpacing="0.15em"
            textTransform="uppercase"
          >
            Swimmers welcome · Family welcome · Absolutely no pressure to swim
          </Text>

        </VStack>
      </Container>
    </Box>
  );
};

export default BdayPage;
